import {
  availableMinutes,
  band,
  committedMinutes,
  deadlineFeasibility,
  hasBreach,
  utilisation,
} from './capacity';
import { BLOCKED_STATUS, TASK_STATUS } from './constants';

const ownerTasks = (tasks, ownerName) => {
  const name = `${ownerName || ''}`.trim().toLowerCase();
  if (!name) return [];
  return (tasks || []).filter((t) => `${t?.owner || ''}`.trim().toLowerCase() === name);
};

const asClaimed = (task, ownerName) => ({
  id: task?.id,
  typeId: task?.typeId,
  due: task?.due,
  owner: ownerName,
  status: TASK_STATUS.New,
  blockedStatus: BLOCKED_STATUS.Unblocked,
});

export const currentLoad = (tasks, horizon, ownerName, role, dailyMinutes, weeklyMinutes) => {
  const owned = ownerTasks(tasks, ownerName);
  const committed = committedMinutes(owned, horizon, role);
  const available = availableMinutes(horizon, dailyMinutes, weeklyMinutes);
  const util = utilisation(committed, available);
  return { committed, available, util, band: band(util, hasBreach(owned, role, dailyMinutes)) };
};

export const projectClaim = (
  tasks,
  task,
  horizon,
  ownerName,
  role,
  dailyMinutes,
  weeklyMinutes,
) => {
  const current = currentLoad(tasks, horizon, ownerName, role, dailyMinutes, weeklyMinutes);
  const withClaim = [
    ...ownerTasks(tasks, ownerName).filter((t) => `${t.id}` !== `${task.id}`),
    asClaimed(task, ownerName),
  ];
  const committed = committedMinutes(withClaim, horizon, role);
  const available = availableMinutes(horizon, dailyMinutes, weeklyMinutes);
  const util = utilisation(committed, available);
  const projected = {
    committed,
    available,
    util,
    band: band(util, hasBreach(withClaim, role, dailyMinutes)),
  };
  return {
    current,
    projected,
    deltaMinutes: projected.committed - current.committed,
    band: projected.band,
  };
};

export const detectNewBreach = (tasks, task, ownerName, role, dailyMinutes) => {
  const base = ownerTasks(tasks, ownerName).filter((t) => `${t.id}` !== `${task.id}`);
  const withClaim = [...base, asClaimed(task, ownerName)];

  const missedBefore = new Set(
    deadlineFeasibility(base, role, dailyMinutes)
      .filter((f) => f.willMiss)
      .map((f) => f.taskId),
  );
  const newlyMissedList = deadlineFeasibility(withClaim, role, dailyMinutes)
    .filter((f) => f.willMiss && !missedBefore.has(f.taskId))
    .sort((a, b) => a.daysUntilDue - b.daysUntilDue);
  const newlyMissed = newlyMissedList[0];

  if (!newlyMissed) return { hasNewBreach: false, count: 0 };
  return {
    hasNewBreach: true,
    count: newlyMissedList.length,
    taskId: newlyMissed.taskId,
    due: newlyMissed.due,
    daysUntilDue: newlyMissed.daysUntilDue,
    projectedPct: Math.round((newlyMissed.cumulativeMinutes / newlyMissed.capacityByDue) * 100),
  };
};

export const nextCrunch = (tasks, ownerName, role, dailyMinutes) =>
  deadlineFeasibility(ownerTasks(tasks, ownerName), role, dailyMinutes)
    .filter((f) => f.willMiss)
    .sort((a, b) => a.daysUntilDue - b.daysUntilDue)[0] ?? null;
